import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Radios,
  Button,
  Input,
  Hint,
  Fieldset,
  ErrorSummary,
} from 'govuk-react-jsx';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';

import {
  getSubcontractorOzevInstaller,
  getWhoCompletedInstallation,
  setWhoCompletedInstallation,
  getWhoCompletedPreviousAnswer,
  setWhoCompletedPreviousAnswer,
  setSubcontractorOzevNumber,
  setSubcontractorDetails,
} from '../../features/installer/installerJourneySlice';

import {
  getApplicationDocumentId,
  setInstallerDetails,
} from '../../features/application/applicationJourneySlice';

import { checkValidSubcontractorOzevNumber } from '../../regex/regex';
import {
  checkSubcontractorOZEVNumber,
  checkStatus,
  deleteSubcontractorDetails,
  getInstallerDetails,
} from '../../firebase';
import useTitle from '../../hooks/useTitle';

// Helper function to handle Firebase error responses
const handleFirebaseError = (
  error,
  setErrorMsg,
  setErrorList,
  defaultError
) => {
  if (error && error.message) {
    if (error.message.includes('Installer not authorised for this grant')) {
      const errorMessage = 'Installer not authorised for this grant';
      setErrorMsg({ children: errorMessage });
      setErrorList([
        {
          children: errorMessage,
          href: '#subcontractor',
        },
      ]);
      return true;
    }

    if (
      error.message.includes('Installer is not active or new') ||
      error.code === 'permission-denied' ||
      error.status === 403 ||
      (error.response && error.response.status === 403)
    ) {
      const errorMessage = 'Installer is not active or new';
      setErrorMsg({ children: errorMessage });
      setErrorList([
        {
          children: errorMessage,
          href: '#subcontractor',
        },
      ]);
      return true;
    }
  }

  if (defaultError) {
    setErrorMsg(defaultError);
    setErrorList([{ ...defaultError, href: '#subcontractor' }]);
  }
  return false;
};

const Subcontractor = ({ location }) => {
  const isFromCheckAnswers = location?.state?.isFromCheckAnswers;
  const [errorMsg, setErrorMsg] = useState();
  const [errorList, setErrorList] = useState([]);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const applicationDocumentId = useSelector(getApplicationDocumentId);
  const [isTrue, setIsTrue] = useState(false);

  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  useTitle(
    'installer.Subcontractor.s10',
    'Who completed the installation? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );
  const subcontractorOzevNumberRedux = useSelector(
    getSubcontractorOzevInstaller
  );
  const whoCompletedInstallationRedux = useSelector(
    getWhoCompletedInstallation
  );

  const WhoCompletedPreviousAnswerRedux = useSelector(
    getWhoCompletedPreviousAnswer
  );

  const documentId = useSelector(getApplicationDocumentId);
  const dispatch = useDispatch();
  const history = useHistory();

  const blankError = {
    children: copyProvider.getCopy(
      'installer.Subcontractor.s6',
      'Select who completed the installation'
    ),
  };

  const invalidFormatError = {
    children: copyProvider.getCopy(
      'installer.Subcontractor.s7',
      'Enter a valid OZEV number which contains a minimum of 3 and a maximum of 5 numeric characters'
    ),
  };
  const apiError = {
    children: copyProvider.getCopy(
      'installer.Subcontractor.s0',
      'You must select a subcontractor'
    ),
  };

  const subcontracotrAIR = {
    children: copyProvider.getCopy(
      'installerApp.ResponsibleForInstallation.s11',
      'Subcontractor nominated should not be an Authorised Installer Representative'
    ),
  };

  const invalidOzevNumber = {
    children: copyProvider.getCopy(
      'installer.Subcontractor.s8',
      'Installer not authorised for this grant'
    ),
  };

  const handleSelectionBtn = (event) => {
    if (
      event.target.value === 'A subcontractor' ||
      event.target.value === subcontractorOzevNumberRedux
    ) {
      dispatch(setWhoCompletedInstallation('A subcontractor'));
    } else {
      dispatch(setWhoCompletedInstallation('An employee'));
      dispatch(setSubcontractorOzevNumber(''));
    }
  };

  const handleSubcontractorOzevNumber = (event) => {
    dispatch(setSubcontractorOzevNumber(event.target.value));
  };

  const handleBtn = () => {
    if (
      WhoCompletedPreviousAnswerRedux === whoCompletedInstallationRedux &&
      isFromCheckAnswers
    ) {
      dispatch(setWhoCompletedPreviousAnswer(whoCompletedInstallationRedux));
      history.goBack();
    } else if (!whoCompletedInstallationRedux) {
      setErrorMsg(blankError);
      setErrorList([{ ...blankError, href: '#installer-subcontractor' }]);
    } else if (whoCompletedInstallationRedux === 'A subcontractor') {
      setErrorMsg(null);
      setErrorList([]);
      if (
        checkValidSubcontractorOzevNumber.test(subcontractorOzevNumberRedux)
      ) {
        setErrorMsg(null);
        checkSubcontractorOZEVNumber({
          subcontractorOZEVInstallerNumber: subcontractorOzevNumberRedux,
          applicationDocumentId,
        })
          .then((response) => {
            const { data } = response;
            if (
              data !== null &&
              data?.subcontractorData?.installer_number ===
                subcontractorOzevNumberRedux &&
              data?.success === true
            ) {
              dispatch(
                setSubcontractorOzevNumber(
                  data?.subcontractorOZEVInstallerNumber
                )
              );
              dispatch(
                setSubcontractorDetails({
                  subcontractor_number:
                    data?.subcontractorData?.installer_number,
                  subcontractor_name: data?.subcontractorData?.company_name,
                })
              );
              dispatch(setInstallerDetails(data));
              if (response.data.subcontractorData.installer_representative) {
                setErrorMsg(subcontracotrAIR);
                setErrorList([{ ...subcontracotrAIR, href: '#subcontractor' }]);
                return;
              }
              history.push({
                pathname: '/installer/confirm-subcontractor-details',
                state: { isFromCheckAnswers: true },
              });
            } else {
              setErrorMsg(invalidOzevNumber);
              setErrorList([{ ...invalidOzevNumber, href: '#subcontractor' }]);
            }
          })
          .catch((error) => {
            handleFirebaseError(
              error,
              setErrorMsg,
              setErrorList,
              invalidOzevNumber
            );
          });
      } else {
        setErrorMsg(invalidFormatError);
        setErrorList([{ ...invalidFormatError, href: '#subcontractor' }]);
      }
    } else {
      getInstallerDetails()
        .then((response) => {
          if (response.data.success) {
            if (response.data.isRepresentative) {
              setErrorMsg(apiError);
              setErrorList([{ ...apiError, href: '#employee' }]);
            } else {
              deleteSubcontractorDetails({ applicationDocumentId })
                .then(() => {
                  dispatch(
                    setSubcontractorDetails({
                      subcontractor_number: '',
                      subcontractor_name: '',
                    })
                  );
                  history.push('/installer/installation-date');
                })
                .catch(() => {
                  // Handle error
                });
              setErrorMsg(null);
              setIsTrue(true);
            }
          }
        })
        .catch((err) => {
          // TODO: Handle error later
          // eslint-disable-next-line
          console.error(err);
        });
    }
  };

  // eslint-disable-next-line
  isTrue &&
    checkStatus({
      id: documentId,
    })
      .then((response) => {
        setIsTrue(false);
        if (!response.data.installer_representative) {
          dispatch(
            setInstallerDetails(response.data.installerDetailsFromAppClaim)
          );
          history.push('/installer/installation-date');
        } else {
          setErrorMsg(apiError);
          setErrorList([{ ...apiError, href: '#subcontractor' }]);
        }
      })
      .catch(() => {
        setErrorMsg(apiError);
        setErrorList([{ ...apiError, href: '#subcontractor' }]);
      });

  const pageModel = (
    <>
      {errorList.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <Fieldset
        legend={{
          children: (
            <h1 className="govuk-fieldset__heading">
              {copyProvider.getCopy(
                'installer.Subcontractor.s1',
                'Who completed the installation?'
              )}
            </h1>
          ),
          className: 'govuk-fieldset__legend--xl',
          isPageHeading: true,
        }}
      >
        <Radios
          idPrefix="installer-subcontractor"
          errorMessage={errorMsg || null}
          value={whoCompletedInstallationRedux}
          onChange={handleSelectionBtn}
          items={[
            {
              children: `${copyProvider.getCopy(
                'installer.Subcontractor.s2',
                'An employee of your company'
              )}`,
              value: 'An employee',
            },
            {
              children: copyProvider.getCopy(
                'installer.Subcontractor.s3',
                'A subcontractor'
              ),
              value: 'A subcontractor',
              conditional: {
                children: [
                  <Hint>
                    {copyProvider.getCopy(
                      'installer.Subcontractor.s9',
                      'Subcontractors must be approved by OZEV and need to have their own installer number. If you do not know their installer number, please contact your subcontractor.'
                    )}
                  </Hint>,
                  <Input
                    label={{
                      children: copyProvider.getCopy(
                        'installer.Subcontractor.s4',
                        'Enter the subcontractor’s OZEV Installer number'
                      ),
                    }}
                    alt="Installer number"
                    key="1"
                    id="subcontractor"
                    className="govuk-input govuk-!-width-one-third"
                    name="subcontractor"
                    type="number"
                    value={subcontractorOzevNumberRedux}
                    onChange={(event) => {
                      handleSubcontractorOzevNumber(event);
                    }}
                  />,
                ],
              },
            },
          ]}
          name="installer-subcontractor"
        />
      </Fieldset>
      <Button className="govuk-button" onClick={handleBtn}>
        {copyProvider.getCopy(
          'installer.Subcontractor.s5',
          'Save and continue'
        )}
      </Button>
    </>
  );
  return <> {pageModel} </>;
};

Subcontractor.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      isFromCheckAnswers: PropTypes.bool,
    }),
  }),
};

Subcontractor.defaultProps = {
  location: {
    state: {
      isFromCheckAnswers: false,
    },
  },
};

export default Subcontractor;
